// @flow

import type {SearchRequest} from './models/SearchRequest';
import type {Brand} from './models/Brand';

import SearchUrlParser from './parsers/SearchUrlParser';
import type {Options} from './parsers/SearchUrlParser';
import SERPSearchUrlParser from './parsers/SERPSearchUrlParser';
import GTSearchUrlParser from './parsers/GTSearchUrlParser';
import VRBOSearchUrlParser from './parsers/VRBOSearchUrlParser';
import AbritelSearchUrlParser from './parsers/AbritelSearchUrlParser';
import BrazilSearchUrlParser from './parsers/BrazilSearchUrlParser';
import petSyncer from './utils/petSyncer';

/**
 * Determines whether path matches SERP url pattern
 * SERP path prefix always followed by query parameter
 * Valid SERP urls: '/s?q=Keyword', '/serp?q=Keyword'
 * Not valid: '/search|semester-stugor-laegenhet'
 * @private
 * @param pathname
 * @returns {boolean}
 */
function isSERPUrl(pathname: string): boolean {
    return !!pathname.match(/\/(s(?!\w)|serp)/); //
}

const HA_DEFAULT_PARSER = new GTSearchUrlParser();

/**
 * Brand specific parsers
 * @private
 */
const BRAND_PARSERS = {
    vrbo: new VRBOSearchUrlParser(),
    abritel: new AbritelSearchUrlParser(),
    homeaway_br: new BrazilSearchUrlParser()
};

/**
 * Parses search page URLs into an instance of {@link SearchRequest}. Handles /results, /search, /s, and most legacy SEO URLs. Refer to {@link LANDING_PAGE_URL_PREFIXES}
 * for a list of supported legacy paths.
 *
 * @example
 * parseURL(
 *      '/search/keywords:austin-tx-usa?petInclued=true&filter=38',
 *      {
 *          name: 'vrbo'
 *      });
 * // returns
 * // {
 * //   q: 'austin-tx-usa',
 * //   filter: ['filter:38'],
 * //   petInclued: true
 * // }
 *
 * @param {string} url - search page url, either relative or absolute
 * @param {object} brand - the brand to use
 * @param {string} [brand.name] - the site name to use (homeaway_us, abritel, homeaway_de, vrbo, etc.)
 * @param {object} options - Options
 * @param {function} [options.onWarning] - Function called for malformed URIs
 * @returns {SearchRequest} - corresponding to the given search page URL
 */
export default function parseURL(
    url: string,
    brand: Brand,
    options: Options = {
        onWarning: () => {}
    }
): SearchRequest {
    let parser: SearchUrlParser;
    let searchRequest = {};
    const brandName = brand.name.toLowerCase();

    if (isSERPUrl(url)) {
        // new URLs
        parser = new SERPSearchUrlParser();
    } else {
        parser = BRAND_PARSERS[brandName] || HA_DEFAULT_PARSER;
    }

    try {
        const decodedUrl = decodeURI(url);
        searchRequest = parser.parse(decodedUrl, options);

        if (brandName === 'vrbo') {
            try {
                const haSearchRequest = HA_DEFAULT_PARSER.parse(
                    decodedUrl,
                    options
                );

                if (
                    Object.keys(haSearchRequest).length >
                    Object.keys(searchRequest).length
                ) {
                    searchRequest = haSearchRequest;
                }
            } catch (e) {} // eslint-disable-line no-empty
        }
    } catch (e) {
        if (options.onWarning) {
            if (e instanceof URIError) {
                options.onWarning(`Malformed URL = {${url}}`);
            } else {
                options.onWarning(e.message);
            }
        }
    }

    // sync pets
    return petSyncer(searchRequest);
}
